import { useState } from 'react';
import { format } from 'date-fns';
import dayjs, { Dayjs } from 'dayjs';

import { Box, Button, Container, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { paths } from 'src/routes/paths';

import {
  sendExciseReportEmail,
  useGetAdministrationStats,
  useGetAgingPayables,
  useGetAgingReceivables,
  useGetCurrentMonthStats,
  useGetLogisticsStats,
} from 'src/api/admin';

import { useLocales } from 'src/locales';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs/custom-breadcrumbs';
import { useSettingsContext } from 'src/components/settings';
import { useSnackbar } from 'src/components/snackbar';

import LogisticsSection from 'src/sections/admin/overview/view/logistics-section';
import AdministrationSection from 'src/sections/admin/overview/view/administration-section';

import MonthlyStatsCards from '../thermometer/monthly-stats-cards';
import ReceivablesSection from '../receivables/receivables-section';
import PayablesSection from '../payables/payables-section';

export default function OperationsDashboardView() {
  const { t } = useLocales('operativaDashboard');
  const settings = useSettingsContext();
  const { enqueueSnackbar } = useSnackbar();

  const currentDate = format(new Date(), 'yyyy-MM-dd');
  const [exciseReportDate, setExciseReportDate] = useState<Dayjs | null>(dayjs());

  const { data: logisticsData } = useGetLogisticsStats({ currentDate });
  const { data: administrationData } = useGetAdministrationStats();
  const { data: currentMonthData } = useGetCurrentMonthStats();
  const { data: receivablesData } = useGetAgingReceivables();
  const { data: payablesData } = useGetAgingPayables();

  const emailExciseReport = async () => {
    try {
      const dateToSend = exciseReportDate ? exciseReportDate.format('YYYY-MM-DD') : currentDate;
      await sendExciseReportEmail({ date: dateToSend });
      enqueueSnackbar(t('excise_report_sent'), { variant: 'success' });
    } catch (error) {
      const message = error?.response?.data?.message || error.message;
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  return (
    <Container maxWidth={settings.state.compactLayout ? 'xl' : false}>
      <CustomBreadcrumbs
        heading={t('operativa')}
        links={[{ name: t('admin'), href: paths.admin.root }, { name: t('operativa') }]}
        sx={{ mb: [3, 3, 5] }}
      />

      <Box display="flex" flexDirection="column" gap={6}>
        {/* Section 1 — Termometro */}
        <Box display="flex" flexDirection="column" gap={3}>
          <Typography variant="h4">{t('termometro')}</Typography>
          {currentMonthData && <MonthlyStatsCards currentMonthData={currentMonthData} />}
        </Box>

        {/* Section 2 — Logistica */}
        <Box display="flex" flexDirection="column" gap={3} sx={{ overflowX: 'auto' }}>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h4">{t('logistica')}</Typography>
            <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
              <DatePicker
                label={t('excise_report_date')}
                value={exciseReportDate}
                onChange={(newValue) => setExciseReportDate(newValue)}
                slotProps={{ textField: { size: 'small' } }}
              />
              <Button onClick={emailExciseReport} variant="contained">
                {t('email_excise_report')}
              </Button>
            </Box>
          </Box>
          {logisticsData && <LogisticsSection {...logisticsData} />}
        </Box>

        {/* Section 3 — Da fatturare */}
        <Box display="flex" flexDirection="column" gap={3} sx={{ overflowX: 'auto' }}>
          <Typography variant="h4">{t('da_fatturare')}</Typography>
          {administrationData && <AdministrationSection administrationData={administrationData} />}
        </Box>

        {/* Section 4 — Da incassare */}
        <Box display="flex" flexDirection="column" gap={3}>
          <Typography variant="h4">{t('da_incassare')}</Typography>
          {receivablesData?.buckets && <ReceivablesSection data={receivablesData} />}
        </Box>

        {/* Section 5 — Da pagare */}
        <Box display="flex" flexDirection="column" gap={3}>
          <Typography variant="h4">{t('da_pagare')}</Typography>
          {payablesData?.buckets && <PayablesSection data={payablesData} />}
        </Box>
      </Box>
    </Container>
  );
}
